import { useEffect, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAuth } from "../../auth/store";
import { useCan } from "../../auth/roles";
import { ErrorState } from "../../components/States";
import { BusyButton, ConfirmDialog, CopyButton, DataTable, Dialog, Field, RowMenu, Switch, useToast } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { backendApi, backendKeys, type BackendSettings, type CaptureLevel, type ServerKey } from "./api";

/** A key the caller just made. Its secret is shown once and then only its prefix. */
export interface NewKey {
  key: ServerKey;
  secret: string;
}

const LEVELS: Array<{ id: CaptureLevel; label: string; hint: string }> = [
  { id: "everything", label: "Everything", hint: "Every request and every log line, including debug." },
  { id: "standard", label: "Standard", hint: "Every request, and info logs and above." },
  { id: "critical", label: "Problems only", hint: "Only requests that fail, run slow, or come from a replay, with their warnings and errors." },
  { id: "off", label: "Off", hint: "Keep nothing. Services stay connected and can be turned back on here." },
];

const FLOORS = [
  { id: "", label: "Follow what to keep" },
  { id: "debug", label: "Debug and above" },
  { id: "info", label: "Info and above" },
  { id: "warn", label: "Warnings and above" },
  { id: "error", label: "Errors and above" },
  { id: "fatal", label: "Fatal only" },
];

const DETECTORS: Array<{ id: string; label: string }> = [
  { id: "email", label: "Email addresses" },
  { id: "phone", label: "Phone numbers" },
  { id: "card", label: "Card numbers" },
  { id: "ssn", label: "Social Security numbers" },
  { id: "iban", label: "Bank account numbers (IBAN)" },
  { id: "ip", label: "IP addresses" },
  { id: "jwt", label: "Sign-in tokens" },
  { id: "bearer", label: "Authorization headers" },
  { id: "api_key", label: "Secret keys" },
  { id: "url_credentials", label: "Passwords in links" },
];

export function levelLabel(level?: string): string {
  return LEVELS.find((l) => l.id === level)?.label ?? "Standard";
}

interface KeepDraft {
  captureLevel: CaptureLevel;
  severityFloor: string;
  captureBodies: boolean;
  phi: boolean;
  hashUserIds: boolean;
  disabled: string[];
  fields: string;
}

function keepOf(s: BackendSettings): KeepDraft {
  return {
    captureLevel: s.captureLevel,
    severityFloor: s.severityFloor ?? "",
    captureBodies: s.captureBodies,
    phi: !!s.redaction.phi,
    hashUserIds: !!s.redaction.hashUserIds,
    disabled: s.redaction.disabledDetectors ?? [],
    fields: (s.redaction.fields ?? []).join("\n"),
  };
}

function sameKeep(a: KeepDraft, b: KeepDraft): boolean {
  return a.captureLevel === b.captureLevel && a.severityFloor === b.severityFloor && a.captureBodies === b.captureBodies && a.phi === b.phi && a.hashUserIds === b.hashUserIds && a.fields === b.fields && a.disabled.length === b.disabled.length && a.disabled.every((id) => b.disabled.includes(id));
}

/** Backend keys for every app in the organization, with what each app keeps. */
export function OrgBackendKeys({ projects }: { projects: Array<{ id: string; name: string }> }) {
  const can = useCan();
  const auth = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const nameOf = new Map(projects.map((p) => [p.id, p.name]));
  const lists = useQueries({
    queries: projects.map((p) => ({ queryKey: backendKeys.keys(p.id), queryFn: () => backendApi.keys(p.id) })),
  });
  const settings = useQueries({
    queries: projects.map((p) => ({ queryKey: backendKeys.settings(p.id), queryFn: () => backendApi.settings(p.id) })),
  });
  const levelOf = new Map(projects.map((p, i) => [p.id, settings[i]?.data?.settings.captureLevel]));
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<NewKey | null>(null);
  const [revoking, setRevoking] = useState<ServerKey | null>(null);
  const [editing, setEditing] = useState<ServerKey | null>(null);

  const rows = projects.flatMap((p, i) => (lists[i]?.data?.keys ?? []).filter((k) => !k.revokedAt).map((k) => ({ ...k, app: p.name })));
  const failed = lists.find((q) => q.isError);
  const loading = projects.length > 0 && lists.some((q) => q.isPending);

  const revoke = useMutation({
    mutationFn: (k: ServerKey) => backendApi.revokeKey(k.projectId, k.id),
    onSuccess: (_d, k) => {
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: backendKeys.keys(k.projectId) });
      toast.push("info", "Backend key revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <>
      <div className="be-keys-head">
        <p className="muted small">A service reads this when it starts. The secret is shown once, when the key is created. What an app keeps is shared by every backend key for that app.</p>
        {can.admin && <button type="button" className="btn primary" onClick={() => setOpen(true)} disabled={projects.length === 0}>New backend key</button>}
      </div>
      {failed ? (
        <ErrorState error={failed.error} retry={() => void lists.forEach((q) => q.refetch())} />
      ) : (
        <div className="stack-table be-table">
          <DataTable<ServerKey & { app: string }>
            ariaLabel="Backend keys"
            rows={rows}
            rowKey={(k) => k.id}
            loading={loading}
            empty={projects.length === 0 ? "Create a browser key first. A backend key belongs to an app." : "No backend keys yet."}
            columns={[
              { key: "name", header: "Name", label: "Name", render: (k) => (<><strong>{k.name || "Backend key"}</strong><div className="muted small mono">{k.prefix}…</div></>) },
              { key: "app", header: "App", label: "App", render: (k) => k.app },
              { key: "env", header: "Environment", label: "Environment", render: (k) => (k.environment ? <span className="badge neutral">{k.environment}</span> : <span className="muted">Any</span>) },
              { key: "keep", header: "What it keeps", label: "What it keeps", render: (k) => <span className="badge neutral">{levelLabel(levelOf.get(k.projectId))}</span> },
              { key: "used", header: "Last used", label: "Last used", render: (k) => (k.lastUsedAt ? formatRelative(k.lastUsedAt) : <span className="muted">Never</span>) },
              {
                key: "act",
                header: "",
                align: "right",
                render: (k) => (
                  <RowMenu label={`Actions for ${k.name || k.prefix}`}>
                    <button type="button" role="menuitem" onClick={() => setEditing(k)}>What it keeps</button>
                    {can.admin && <button type="button" role="menuitem" className="danger" onClick={() => setRevoking(k)}>Revoke key</button>}
                  </RowMenu>
                ),
              },
            ]}
          />
        </div>
      )}

      <CreateKeyDialog
        open={open}
        projects={projects}
        onClose={() => setOpen(false)}
        onCreated={(k) => {
          setOpen(false);
          setCreated(k);
          void qc.invalidateQueries({ queryKey: backendKeys.keys(k.key.projectId) });
        }}
      />
      <Dialog open={!!created} onClose={() => setCreated(null)} title="Backend key created" footer={<button type="button" className="btn primary" onClick={() => setCreated(null)}>Done</button>}>
        <p className="muted">Copy it now. For security it is shown only once.</p>
        <div className="mono-box" data-testid="server-key-secret">{created?.secret}</div>
        <div style={{ marginTop: 8 }}>
          <CopyButton value={created?.secret ?? ""} label="Copy key" />
        </div>
      </Dialog>
      <KeepDialog
        app={editing ? nameOf.get(editing.projectId) ?? "this app" : ""}
        projectId={editing?.projectId}
        canEdit={can.admin}
        orgId={auth.currentOrgId}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!revoking}
        danger
        title="Revoke backend key"
        confirmLabel="Revoke key"
        busy={revoke.isPending}
        onClose={() => { if (!revoke.isPending) setRevoking(null); }}
        onConfirm={() => revoking && revoke.mutate(revoking)}
      >
        <p style={{ margin: 0 }}><strong>{revoking?.name || revoking?.prefix}</strong> stops working immediately, and services still using it stop sending data.</p>
      </ConfirmDialog>
    </>
  );
}

function KeepDialog({ app, projectId, canEdit, orgId, onClose }: { app: string; projectId?: string; canEdit: boolean; orgId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: backendKeys.settings(projectId), queryFn: () => backendApi.settings(projectId!), enabled: !!projectId });
  const [draft, setDraft] = useState<KeepDraft | null>(null);
  const [base, setBase] = useState<BackendSettings | null>(null);
  const [saved, setSaved] = useState<KeepDraft | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | undefined>();
  const [discard, setDiscard] = useState(false);
  useEffect(() => {
    if (!projectId) {
      setLoadedFor(undefined);
      setDraft(null);
      setSaved(null);
      setBase(null);
      return;
    }
    if (!q.data || loadedFor === projectId) return;
    const next = keepOf(q.data.settings);
    setLoadedFor(projectId);
    setBase(q.data.settings);
    setDraft(next);
    setSaved(next);
  }, [projectId, q.data, loadedFor]);
  const dirty = !!draft && !!saved && !sameKeep(draft, saved);
  const save = useMutation({
    mutationFn: () => {
      if (!draft || !base || !projectId) throw new Error("Nothing to save");
      const fields = draft.fields.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      return backendApi.saveSettings(projectId, {
        ...base,
        captureLevel: draft.captureLevel,
        severityFloor: draft.severityFloor || undefined,
        captureBodies: draft.phi ? false : draft.captureBodies,
        redaction: { ...base.redaction, phi: draft.phi, hashUserIds: draft.phi || draft.hashUserIds, disabledDetectors: draft.disabled, fields },
      });
    },
    onSuccess: (res) => {
      const next = keepOf(res.settings);
      setBase(res.settings);
      setDraft(next);
      setSaved(next);
      void qc.invalidateQueries({ queryKey: backendKeys.settings(projectId) });
      toast.push("success", "Saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const askClose = () => {
    if (dirty && canEdit) setDiscard(true);
    else onClose();
  };
  const set = (patch: Partial<KeepDraft>) => draft && setDraft({ ...draft, ...patch });
  return (
    <>
      <Dialog
        open={!!projectId}
        side
        className="project-settings"
        onClose={askClose}
        title={app ? `What ${app} keeps` : "What it keeps"}
        footer={canEdit ? (
          <>
            <button type="button" className="btn" onClick={askClose}>Cancel</button>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" disabled={!dirty || !draft} onClick={() => save.mutate()}>Save changes</BusyButton>
          </>
        ) : <button type="button" className="btn" onClick={onClose}>Close</button>}
      >
        {q.isError ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : null}
        {draft && (
          <>
            <p className="muted small">Every backend key for {app} uses this. A service picks it up within a minute.</p>
            <Field label="What to keep" hint={LEVELS.find((l) => l.id === draft.captureLevel)?.hint}>
              {(p) => (
                <select {...p} className="input" value={draft.captureLevel} disabled={!canEdit} onChange={(e) => set({ captureLevel: e.target.value as CaptureLevel })}>
                  {LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              )}
            </Field>
            <Field label="Log lines">
              {(p) => (
                <select {...p} className="input" value={draft.severityFloor} disabled={!canEdit} onChange={(e) => set({ severityFloor: e.target.value })}>
                  {FLOORS.map((f) => <option key={f.id || "follow"} value={f.id}>{f.label}</option>)}
                </select>
              )}
            </Field>
            <Switch checked={draft.captureBodies && !draft.phi} disabled={!canEdit || draft.phi} onChange={(v) => set({ captureBodies: v })} label="Keep request and response bodies" hint={draft.phi ? "Off while protected health information mode is on." : "Bodies are checked for personal data before they are stored."} />
            <Switch checked={draft.phi} disabled={!canEdit} onChange={(v) => set({ phi: v, captureBodies: v ? false : draft.captureBodies, hashUserIds: v ? true : draft.hashUserIds })} label="These services handle protected health information" hint="Bodies are never kept, patient fields are hidden, and user IDs are replaced with stable codes." />
            <Switch checked={draft.phi || draft.hashUserIds} disabled={!canEdit || draft.phi} onChange={(v) => set({ hashUserIds: v })} label="Replace user IDs with stable codes" hint="The same person keeps the same code, so you can still follow one user's requests." />
            <fieldset className="be-detectors">
              <legend>Find and hide automatically</legend>
              {DETECTORS.map((d) => (
                <label key={d.id}>
                  <input type="checkbox" checked={!draft.disabled.includes(d.id)} disabled={!canEdit} autoComplete="off" onChange={(e) => set({ disabled: e.target.checked ? draft.disabled.filter((id) => id !== d.id) : [...draft.disabled, d.id] })} />
                  {d.label}
                </label>
              ))}
            </fieldset>
            <Field label="More fields to hide" hint="One per line. Matched by name anywhere in a request or log, on top of passwords, tokens, and card fields.">
              {(p) => <textarea {...p} className="input" rows={3} value={draft.fields} disabled={!canEdit} onChange={(e) => set({ fields: e.target.value })} placeholder={"member_number\nloyalty_id"} />}
            </Field>
            {orgId && <p className="muted small">Routes to skip, headers, and the monthly limit are in <Link to={"/backend/settings" as never}>Backend settings</Link>.</p>}
          </>
        )}
      </Dialog>
      <ConfirmDialog open={discard} title="Discard changes" confirmLabel="Discard" onClose={() => setDiscard(false)} onConfirm={() => { setDiscard(false); onClose(); }}>
        <p style={{ margin: 0 }}>Unsaved changes to what this app keeps are discarded.</p>
      </ConfirmDialog>
    </>
  );
}

function CreateKeyDialog({ open, onClose, onCreated, projects }: { open: boolean; onClose: () => void; onCreated: (k: NewKey) => void; projects?: Array<{ id: string; name: string }> }) {
  const auth = useAuth();
  const toast = useToast();
  const [projectId, setProjectId] = useState("");
  const [name, setName] = useState("");
  const [env, setEnv] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const chosen = projects?.length === 1 ? projects[0]!.id : projectId || (projects ? "" : auth.currentProjectId ?? "");
  const nameErr = name.trim().length > 80 ? "Use 80 characters or fewer." : errors.name;
  const envErr = env.trim().length > 40 ? "Use 40 characters or fewer." : errors.environment;
  const create = useMutation({
    mutationFn: () => backendApi.createKey(chosen, { name: name.trim(), environment: env.trim() }),
    onSuccess: (k) => {
      setName("");
      setEnv("");
      setErrors({});
      onCreated(k);
    },
    onError: (e) => {
      const err = e as Error & { field?: string; body?: { field?: string } };
      const field = err.field ?? err.body?.field;
      if (field) setErrors({ [field]: err.message });
      else toast.push("error", err.message);
    },
  });
  const ready = name.trim() !== "" && chosen !== "" && !nameErr && !envErr;
  return (
    <Dialog
      open={open}
      onClose={() => { if (!create.isPending) onClose(); }}
      title="New backend key"
      width={480}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <BusyButton className="btn primary" busy={create.isPending} busyLabel="Creating…" disabled={!ready} onClick={() => create.mutate()}>Create key</BusyButton>
        </>
      }
    >
      {projects && projects.length > 1 && (
        <Field label="App">
          {(p) => (
            <select {...p} className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Choose an app</option>
              {projects.map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}
            </select>
          )}
        </Field>
      )}
      <Field label="Name" error={nameErr} hint="Which service or team uses it.">
        {(p) => <input {...p} className="input" value={name} onChange={(e) => { setName(e.target.value); setErrors({}); }} placeholder="Checkout service" autoFocus />}
      </Field>
      <Field label="Environment" error={envErr} hint="Optional. Leave empty to let the service name its own environment.">
        {(p) => <input {...p} className="input" value={env} onChange={(e) => { setEnv(e.target.value); setErrors({}); }} placeholder="production" />}
      </Field>
    </Dialog>
  );
}
